import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { AnonymousSessionStorage } from 'src/core/jwt/anonymous-session.storage';
import {
  AnonymousExecutionContext,
  AuthenticatedExecutionContext,
  ExecutionContext,
} from './execution-context.types';
import {
  resolveAnonymousContext,
  resolveAuthenticatedContext,
} from './execution-context.resolver';

const LEGACY_ARIDOS_KEY = 'isUserArido';
const LEGACY_PLANT_OPERATOR_KEY = 'isOperadorPlanta';

/**
 * Punto único de resolución del perfil de ejecución.
 *
 * La escritura de las flags legacy se mantiene solo como proyección temporal
 * para los componentes que aún no fueron migrados a sus módulos de perfil.
 */
@Injectable({ providedIn: 'root' })
export class ExecutionContextService {
  private readonly authenticatedSubject =
    new BehaviorSubject<AuthenticatedExecutionContext | null>(null);
  private readonly anonymousSubject =
    new BehaviorSubject<AnonymousExecutionContext | null>(null);

  readonly authenticated$: Observable<AuthenticatedExecutionContext | null> =
    this.authenticatedSubject.asObservable();
  readonly anonymous$: Observable<AnonymousExecutionContext | null> =
    this.anonymousSubject.asObservable();

  constructor(private readonly anonymousSession: AnonymousSessionStorage) {}

  setAuthenticatedUser(
    user: { ROL?: unknown; OPERADOR_DE_PLANTA?: unknown } | null
  ): AuthenticatedExecutionContext | null {
    if (!user) {
      this.clearAuthenticated();
      return null;
    }

    const context = resolveAuthenticatedContext({
      rol: user.ROL,
      operadorDePlanta: user.OPERADOR_DE_PLANTA,
    });
    this.authenticatedSubject.next(context);

    // Compatibilidad temporal: ningún componente nuevo debe leer estas claves.
    localStorage.setItem(LEGACY_ARIDOS_KEY, String(context.business === 'aridos'));
    localStorage.setItem(
      LEGACY_PLANT_OPERATOR_KEY,
      String(context.role === 'operador-planta')
    );
    return context;
  }

  getAuthenticated(): AuthenticatedExecutionContext | null {
    const current = this.authenticatedSubject.value;
    if (current) return current;

    try {
      const rawUser = localStorage.getItem('user');
      if (!rawUser) return null;
      const user = JSON.parse(rawUser) as {
        ROL?: unknown;
        OPERADOR_DE_PLANTA?: unknown;
      };
      return this.setAuthenticatedUser(user);
    } catch {
      return null;
    }
  }

  setAnonymousSession(source: {
    rol?: unknown;
    authorizedTicket?: string | null;
    token?: string | null;
    expiresAt?: number | null;
    permissions?: readonly string[];
  }): AnonymousExecutionContext {
    const context = resolveAnonymousContext(source);
    this.anonymousSubject.next(context);
    return context;
  }

  getAnonymous(): AnonymousExecutionContext | null {
    const current = this.anonymousSubject.value;
    if (current) return current;

    const token = this.anonymousSession.getToken();
    const ticket = this.anonymousSession.getAuthorizedTicket();
    if (!token && !ticket) return null;

    return this.setAnonymousSession({
      rol: this.anonymousSession.getRol(),
      authorizedTicket: ticket,
      token,
      expiresAt: this.readAnonymousExpiration(),
    });
  }

  clearAuthenticated(): void {
    this.authenticatedSubject.next(null);
    localStorage.removeItem(LEGACY_ARIDOS_KEY);
    localStorage.removeItem(LEGACY_PLANT_OPERATOR_KEY);
  }

  clearAnonymous(): void {
    this.anonymousSubject.next(null);
  }

  clearAll(): void {
    this.clearAuthenticated();
    this.clearAnonymous();
  }

  get current(): ExecutionContext | null {
    return this.getAuthenticated() ?? this.getAnonymous();
  }

  private readAnonymousExpiration(): number | null {
    const raw = localStorage.getItem('anonymous_tracking_exp');
    if (!raw) return null;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
  }
}
